import React, { useEffect, useState } from 'react'
import { Menu, X, ChevronRight, Bot, Zap as Lightning, TrendingUp, FileText, Trophy, Users } from 'lucide-react'
import { HeroSection } from './HeroSection'
import { PricingSection } from './PricingSection'
import { TestimonialsSection } from './TestimonialsSection'
import { ABTestDashboard } from '../dev/ABTestDashboard'
import { StockTaggerLogoWhite, StockTaggerLogoBlack } from '../icons'
// import { useAnalytics } from '../../hooks/useAnalytics'

interface LandingPageProps {
  onSignUp: () => void
  onLogin: () => void
  onSelectPlan: (planId: string, billingCycle: 'monthly' | 'annual') => void
}

export const LandingPage: React.FC<LandingPageProps> = ({
  onSignUp,
  onLogin,
  onSelectPlan
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  // const { trackButtonClick, trackConversion, trackPageView } = useAnalytics()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    setMobileMenuOpen(false)
  }

  const handleGetStarted = () => {
    // trackButtonClick('get_started_cta', 'landing_page')
    // trackConversion('landing_to_signup', 0, { source: 'hero_cta' })
    onSignUp()
  }

  const handleWatchDemo = () => {
    // For now, scroll to testimonials to show "demo" of results
    scrollToSection('testimonials')
  }

  const handleSelectPlan = (planId: string, billingCycle: 'monthly' | 'annual') => {
    // Track plan selection
    // trackButtonClick(`select_plan_${planId}`, 'pricing_section')
    // trackConversion('plan_selection', 0, { plan_id: planId, billing_cycle: billingCycle })

    // If it's the free trial, go directly to signup
    if (planId === 'free') {
      onSignUp()
    } else {
      onSelectPlan(planId, billingCycle)
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                {scrolled ? (
                  <StockTaggerLogoBlack className="w-10 h-10" />
                ) : (
                  <StockTaggerLogoWhite className="w-10 h-10" />
                )}
                <span className={`text-xl font-bold ${
                  scrolled ? 'text-gray-900' : 'text-white'
                }`}>
                  StockTagger.ai
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <button
                  onClick={() => scrollToSection('features')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    scrolled 
                      ? 'text-gray-700 hover:text-blue-600' 
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  Features
                </button>
                <button
                  onClick={() => scrollToSection('pricing')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    scrolled 
                      ? 'text-gray-700 hover:text-blue-600' 
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  Pricing
                </button>
                <button
                  onClick={() => scrollToSection('testimonials')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    scrolled 
                      ? 'text-gray-700 hover:text-blue-600' 
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  Reviews
                </button>
              </div>
            </div>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              <button
                onClick={onLogin}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  scrolled 
                    ? 'text-gray-700 hover:text-blue-600' 
                    : 'text-white/90 hover:text-white'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={onSignUp}
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
              >
                <span>Start Free Trial</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-2 rounded-md ${
                  scrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <button
                onClick={() => scrollToSection('features')}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
              >
                Pricing
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
              >
                Reviews
              </button>
              <div className="border-t border-gray-200 pt-4">
                <button
                  onClick={onLogin}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                >
                  Sign In
                </button>
                <button
                  onClick={onSignUp}
                  className="mt-2 w-full bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Start Free Trial
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <HeroSection 
        onGetStarted={handleGetStarted}
        onWatchDemo={handleWatchDemo}
      />

      {/* Features Section (simplified for now) */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Everything You Need to 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Scale Your Photography</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stop spending hours on metadata. Let AI handle the tedious work while you focus on creating amazing photos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'AI-Powered Metadata',
                description: 'Generate SEO-optimized titles, descriptions, and keywords instantly with 99.2% accuracy.',
                icon: <Bot className="w-10 h-10 text-blue-600" />
              },
              {
                title: 'Batch Processing',
                description: 'Process thousands of photos at once. Upload, click generate, and get perfect metadata in minutes.',
                icon: <Lightning className="w-10 h-10 text-yellow-500" />
              },
              {
                title: 'SEO Optimization',
                description: 'Metadata optimized for stock platforms, search engines, and social media discovery.',
                icon: <TrendingUp className="w-10 h-10 text-green-600" />
              },
              {
                title: 'Multiple Export Formats',
                description: 'Export to CSV, JSON, XMP, or directly embed metadata into your image files.',
                icon: <FileText className="w-10 h-10 text-purple-600" />
              },
              {
                title: 'Stock Platform Ready',
                description: 'Optimized for Shutterstock, Getty Images, Adobe Stock, and 50+ other platforms.',
                icon: <Trophy className="w-10 h-10 text-orange-600" />
              },
              {
                title: 'Team Collaboration',
                description: 'Share templates, collaborate on projects, and maintain consistent metadata across your team.',
                icon: <Users className="w-10 h-10 text-indigo-600" />
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection onSelectPlan={handleSelectPlan} />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* FAQ Section (simplified) */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about StockTagger.ai
            </p>
          </div>

          <div className="space-y-8">
            {[
              {
                q: 'How accurate is the AI metadata generation?',
                a: 'Our AI achieves 99.2% accuracy in generating relevant, SEO-optimized metadata. It understands context, composition, and subject matter to create compelling titles and descriptions that actually convert.'
              },
              {
                q: 'Can I use this for stock photography?',
                a: 'Absolutely! StockTagger.ai is specifically optimized for stock platforms like Shutterstock, Getty Images, Adobe Stock, and 50+ others. Our metadata follows each platform\'s best practices.'
              },
              {
                q: 'What happens to my photos? Are they stored?',
                a: 'We prioritize your privacy. Photos are processed securely and never stored on our servers. All processing happens in real-time and images are immediately deleted after metadata generation.'
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Yes! You can cancel your subscription at any time. We also offer a 30-day money-back guarantee if you\'re not completely satisfied with the results.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <StockTaggerLogoWhite className="w-8 h-8" />
                <span className="text-xl font-bold">StockTagger.ai</span>
              </div>
              <p className="text-gray-400 mb-4 max-w-md">
                The fastest way to generate professional, SEO-optimized metadata for your stock photos.
                Trusted by 10,000+ photographers worldwide.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 space-y-2">
            <p>&copy; 2025 StockTagger.ai. All rights reserved.</p>
            <p>
              Made with ❤️ by{' '}
              <a
                href="https://parchastudios.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 hover:underline font-medium transition-colors"
              >
                ParchaStudios
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* A/B Test Dashboard (development only) */}
      {import.meta.env.DEV && <ABTestDashboard />}
    </div>
  )
}